// tradeText.ts — pure helpers for trades (Oct 28, 2026): a sub's visit to a job, like JAC Gutters
// detaching guards before the roof, resetting them after, a new install or a service call. Shared by the
// build-calendar edge function, the portal's trade sheet (which prices the footage Alex types and previews
// the Google event and the homeowner text) and the portal's unit tests (src/lib/tradeText.test.ts). No
// Deno-only or browser-only APIs in here: keep it importable from both. The plan is
// docs/trade-scheduling-plan.md.
//
// Oct 29, 2026 (Greg): JAC charges $2.50 a foot to detach and $2.50 a foot to reset, so a detach or reset visit
// prices its footage at that rate, not at the install rates. Every trade also carries the price to the
// customer: Bardin wants a 30% margin on the subs' work, and a trade short of it (or done at cost, or sold as
// retail after insurance paid for it) goes to Chad on the Commission board.
//
// Oct 29, 2026 (Greg): "will you add soffit/fascia to the optional work. also have jac as the default sub, but
// make available a 'other' sub they would type in." Soffit / fascia is a work of its own (the trim items on
// the sub's list by the foot, plus off-list lines), and a trade can be with a sub typed on the sheet instead
// of one on the subs list: that sub has no price list, so its trade is priced from off-list lines alone.
//
// Oct 29, 2026 (Greg): "No need to show JAC Gutters' price. Just show the 30% floor and do math from there", and
// "the reps only care about the amount that we need to charge homeowner and where their commission starts." The
// floor (the sub's cost / 0.7, priceAtMargin) is the minimum to charge: the sheet and the cards show it per foot
// (floorRate, a label), per line and in total (floorOf, on the sub's amounts, so it never drifts from the cent
// marginShortfall uses), with the dollars above it (upside, paid as commission on a homeowner-billed trade) or
// under it (the shortfall, which joins the deal's non-collect bucket if Chad deducts it). The homeowner's
// agreement and invoice read one line per trade (tradeCustomerLabel), never scope notes, never a sub figure.
//
// Oct 29, 2026 (Greg): "if a trade is added after the homeowner has signed, the addendum is made. could we treat that
// similar to the color confirmation text a homeowner gets ..." A homeowner-billed trade on a signed roof job is an
// add-on the homeowner signs by text link before it is scheduled or billed (addendumApplies, addendumStatusAfter,
// tradeAddendumText: the wording Greg approved, word for word). Migration 20261029g keeps the status on
// trade_item_prices; its trigger is the authority, the helpers here mirror it for the sheet and the server's checks.
//
// The copy under bardin-portal/supabase/functions/build-calendar/ must stay byte-identical to the one next
// to the deployed function, like eventText.ts.

import { fmtLong, fmtMonthDay, fmtShort, fmtShortSpan, isDateOnly, normalizeBuildEnd, rescheduledSummary, stripStatusPrefix } from "./eventText.ts";

// ── The work ──
// Robert's Other Trades events, Jul-Oct 2026: about half hang off a roof build (a detach before it, then a
// reset or a new install after), a third are new installs, the rest service calls. Soffit / fascia (Greg,
// Oct 29) is the trim work JAC also does: fascia and soffit by the foot, before or after the roof if it matters.
// ⚠ Mirror of trade_items_trade_type_check (migration 20261029b).

export type TradeWork = "detach" | "reset" | "new_install" | "service" | "soffit_fascia" | "other";
export const TRADE_WORKS: TradeWork[] = ["detach", "reset", "new_install", "service", "soffit_fascia", "other"];

export const WORK_LABELS: Record<TradeWork, string> = {
  detach: "Detach (before the roof)",
  reset: "Reset (after the roof)",
  new_install: "New install",
  service: "Service / repair",
  soffit_fascia: "Soffit / fascia",
  other: "Other",
};

export function isTradeWork(v: unknown): v is TradeWork {
  return typeof v === "string" && (TRADE_WORKS as string[]).includes(v);
}

export type TradeTiming = "before_build" | "after_build" | "none";
export const TRADE_TIMINGS: TradeTiming[] = ["before_build", "after_build", "none"];

export const TIMING_LABELS: Record<TradeTiming, string> = {
  before_build: "Before the roof",
  after_build: "After the roof",
  none: "Doesn't matter",
};

export function isTradeTiming(v: unknown): v is TradeTiming {
  return typeof v === "string" && (TRADE_TIMINGS as string[]).includes(v);
}

/** Where a trade falls against the roof, unless Alex says otherwise: a detach goes before the build, a reset
 *  after it, and a new install after it when the job has a roof coming (gutters go up after the roof).
 *  Soffit / fascia, service and other have no roof link unless Alex sets one. */
export function defaultTiming(work: TradeWork, roofAhead: boolean): TradeTiming {
  if (work === "detach") return "before_build";
  if (work === "reset") return "after_build";
  if (work === "new_install" && roofAhead) return "after_build";
  return "none";
}

// ── Items and the sub's price list ──
// JAC's list (Greg, Oct 28, 2026) lives in vendor_prices. These keys are the ones the sheet groups and the
// texts put into words; a key a later list adds still prices and shows by its label.

export type PriceUnit = "ft" | "sq" | "quote";

/** One row of a sub's price list (vendor_prices). `price` is null for a quoted item. */
export interface PriceListItem {
  key: string;
  label: string;
  unit: PriceUnit;
  price: number | null;
  sort?: number;
}

/** A vendor_prices row: one item's price from the day a list took effect. */
export interface PriceListRow extends PriceListItem {
  effectiveFrom: string;
}

/** The list in force on a day: each item's newest row on or before it. A trade is priced at the list in
 *  force when it was first priced (trade_item_prices.rates_from), so a new list from Jose never reprices an
 *  old job, even when its footage is edited later. `from` is the newest list date used. */
export function listInEffect(rows: PriceListRow[], onDate: string): { items: PriceListItem[]; from: string | null } {
  const best = new Map<string, PriceListRow>();
  for (const r of rows) {
    if (r.effectiveFrom > onDate) continue;
    const cur = best.get(r.key);
    if (!cur || r.effectiveFrom > cur.effectiveFrom) best.set(r.key, r);
  }
  const inForce = [...best.values()].sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0) || a.label.localeCompare(b.label));
  const from = inForce.reduce<string | null>((m, r) => (m === null || r.effectiveFrom > m ? r.effectiveFrom : m), null);
  return { items: inForce.map(({ key, label, unit, price, sort }) => ({ key, label, unit, price, sort })), from };
}

/** An off-list line: a detach and reset, touch-up paint, anything JAC prices per job. */
export const CUSTOM_ITEM = "custom";

/** What a trade covers and how much (trade_items.items). No money: RMs read it. */
export interface TradeItem {
  key: string;
  qty: number | null;
  /** Alex's words for an off-list line. */
  label?: string | null;
}

/** An item as the sheet sends it: a typed amount only on a quoted or off-list line. */
export interface PriceInput extends TradeItem {
  amount?: number | null;
}

/** A priced line (trade_item_prices.lines), kept as priced so a new list never reprices an old job. */
export interface PriceLine {
  key: string;
  label: string;
  qty: number | null;
  unit: PriceUnit | "job";
  rate: number | null;
  amount: number | null;
  /** The visit the line belongs to; a detach or reset line is priced at that work's rate. */
  work?: TradeWork;
}

/** "work" is a rate for a kind of visit (detach, reset), on the list but never a footage row of its own. */
type ItemGroup = "gutter" | "guard" | "trim" | "siding" | "interior" | "custom" | "work";

const ITEM_GROUPS: Record<string, ItemGroup> = {
  gutters: "gutter", downspouts: "gutter",
  guards: "guard", roll_lock: "guard", smart_flow: "guard", leaf_free: "guard", leaf_free_high_flow: "guard",
  fascia_pine: "trim", soffit_pine: "trim", fascia_hardie: "trim", soffit_hardie: "trim",
  siding: "siding", interior_repair: "interior",
  detach: "work", reset: "work",
};

/** How an item reads in a homeowner text and an event title. */
const ITEM_WORDS: Record<string, string> = {
  gutters: "gutters", downspouts: "downspouts", guards: "gutter guards",
  roll_lock: "gutter guards", smart_flow: "gutter guards", leaf_free: "gutter guards", leaf_free_high_flow: "gutter guards",
  fascia_pine: "fascia", fascia_hardie: "fascia", soffit_pine: "soffit", soffit_hardie: "soffit",
  siding: "siding", interior_repair: "interior repair",
};

/** Names and units for the known items, for anyone who can't read a price list (RMs see a trade's items,
 *  never its prices). A list's own label wins where there is one. */
export const ITEM_LABELS: Record<string, string> = {
  gutters: "Gutters", downspouts: "Downspouts", guards: "Gutter guards", roll_lock: "Roll lock", smart_flow: "Smart flow", leaf_free: "Leaf free",
  leaf_free_high_flow: "Leaf free high flow", fascia_pine: "Fascia (pine)", soffit_pine: "Soffit (pine)",
  fascia_hardie: "Fascia (Hardie)", soffit_hardie: "Soffit (Hardie)", siding: "Siding", interior_repair: "Interior repair",
  detach: "Detach (per foot)", reset: "Reset (per foot)",
};
export const ITEM_UNITS: Record<string, PriceUnit> = { siding: "sq", interior_repair: "quote" };
export const itemUnit = (key: string): PriceUnit => ITEM_UNITS[key] ?? "ft";

export function itemGroup(key: string): ItemGroup | "other" {
  return key === CUSTOM_ITEM ? "custom" : ITEM_GROUPS[key] ?? "other";
}

/** An email as the function and the sheet both check one: the sub typed on the sheet (Greg, Oct 29) can have
 *  an email to invite in JAC's place. The same test as build-calendar's EMAIL, which imports it. */
export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** The same items and footage, in any order: a trade's stored items against what the sheet sends back. The
 *  server refuses an edit by someone kept off prices (Jeremy) that would change them, since the trade's price
 *  stays as it is for them (pre-deploy review of PR #76, Oct 29, 2026). */
export function sameItems(a: TradeItem[], b: TradeItem[]): boolean {
  const sig = (xs: TradeItem[]) => xs.map((i) => `${i.key}|${i.qty ?? ""}|${(i.label ?? "").trim()}`).sort().join("\n");
  return sig(a) === sig(b);
}

const round2 = (n: number) => Math.round(n * 100) / 100;
const MAX_QTY = 100000;
const MAX_AMOUNT = 1000000;

/** The footage lines only: no off-list line and no quoted item. A detach and its reset share one set of
 *  items, and an off-list line ("Touch-up paint $75") is one job, not one per visit, so it is priced on the
 *  detach alone (delta review of PR #76, Oct 29, 2026: it was charged on both visits). */
export function footageOnly(items: PriceInput[]): PriceInput[] {
  return items.filter((i) => i.key !== CUSTOM_ITEM && itemUnit(i.key) !== "quote");
}

/** The items of a visit, as the sheet sends them, from a stored price's lines: the typed amount of a quoted or
 *  off-list line rides along, so a partner visit that isn't being edited keeps its price (delta review, Oct 29). */
export function inputsFromLines(lines: PriceLine[], work?: TradeWork): PriceInput[] {
  return lines.filter((l) => !work || !l.work || l.work === work)
    .map((l) => (l.key === CUSTOM_ITEM ? { key: l.key, qty: null, label: l.label, amount: l.amount } : l.unit === "quote" ? { key: l.key, qty: null, amount: l.amount } : { key: l.key, qty: l.qty }));
}

/** The items the surviving visit of a pair is repriced from when its partner is cancelled: its own lines, plus
 *  any off-list line the pair priced on the other visit. An off-list line is one job for the pair (priced on
 *  the detach alone, or with the install), so it rides to the visit that goes ahead (review of the Oct 29
 *  branch: an other sub's pair whose only line was "Detach and reset guards $350" repriced to nothing when the
 *  detach was cancelled, and a JAC pair lost its "Touch-up paint $75"). `moved` says whether one rode over. */
export function survivorInputs(lines: PriceLine[], work: TradeWork): { inputs: PriceInput[]; moved: boolean } {
  const rode = inputsFromLines(lines.filter((l) => l.key === CUSTOM_ITEM && !!l.work && l.work !== work));
  return { inputs: [...inputsFromLines(lines, work), ...rode], moved: rode.length > 0 };
}

/** The lines of a job's stored price an edit of one visit starts its typed lines from (the sheet's quotes and
 *  off-list lines; the footage comes from the visit's own items). A detach and its reset share one set of items that
 *  the sheet carries whichever visit is edited (the off-list line rides on the detach, footageOnly), so every line.
 *  Any other pair keeps the partner's lines as stored (inputsFromLines, on the sheet and in trade_save), so only this
 *  visit's own: seeded with the partner's too, the sheet priced its off-list line on both visits and the save wrote it
 *  onto this one (review, Oct 30: editing the detach of a detach + new install copied the install's off-list lines
 *  onto the detach). A single visit, or a pair priced before the lines named their visit, starts from every line. */
export function linesToEdit(lines: PriceLine[], work: TradeWork, paired: boolean): PriceLine[] {
  const partnerWork = paired ? lines.find((l) => l.work && l.work !== work)?.work : undefined;
  if (!partnerWork) return lines;
  const dr = (w: TradeWork) => w === "detach" || w === "reset";
  if (dr(work) && dr(partnerWork)) return lines;
  return lines.filter((l) => l.work === work);
}

/** Why an edit can't move a pair priced at a list to a sub typed on the sheet (review, Oct 29): the partner's
 *  footage would reprice against no list. The sheet shows it and keeps Save off; the server refuses with it. */
export const PAIR_LIST_LOCKED = "A pair priced at JAC's list can't move to a typed sub. Cancel it and set the trade up again with the other sub.";

export interface Priced {
  lines: PriceLine[];
  /** The lines priced from the list (by the foot or the square). null when none are. */
  listTotal: number | null;
  /** Every amount known so far: the list lines plus quoted and off-list amounts Alex typed. */
  knownTotal: number | null;
  /** A quoted or off-list line still has no amount. */
  waitingOnQuote: boolean;
  problems: string[];
}

/** Prices the footage Alex typed at the sub's rates. An item with no footage isn't on the job. A quoted item
 *  (interior repair) and an off-list line carry the amount Alex typed, or wait on the sub's quote.
 *  A detach or a reset (Greg, Oct 29: JAC charges $2.50 a foot for each) prices every foot of gutters,
 *  downspouts and guards at that work's rate from the list (its "detach" / "reset" rows), whatever the brand.
 *  A sub typed on the sheet (Greg, Oct 29: an "other" sub) has no list: pass an empty one, and only off-list
 *  lines price; any list key is a problem, which is right, since nobody knows that sub's rates. */
export function priceTrade(items: PriceInput[], list: PriceListItem[], work?: TradeWork): Priced {
  const byKey = new Map(list.map((p) => [p.key, p]));
  const lines: PriceLine[] = [];
  const problems: string[] = [];
  let listSum = 0, listAny = false, typedSum = 0, typedAny = false, waiting = false;
  const dr = work === "detach" || work === "reset";
  const workRate = dr ? byKey.get(work) ?? null : null;
  // A rate row with no price is the list as someone kept off prices sees it (trade_context blanks every
  // price): the footage still prices into lines, just without amounts. Only a list with no detach / reset
  // row at all is a problem (delta review of PR #76, Oct 29, 2026: Jeremy couldn't save any detach or reset),
  // and only once there is footage to price at it: a sub typed on the sheet (Oct 29) has no list, and its
  // detach or reset is one off-list line ("Detach and reset guards $350"), which needs no rate.
  let noRate = false;
  const tag = work ? { work } : {};

  const typed = (raw: number | null | undefined, what: string): number | null | "bad" => {
    if (raw === null || raw === undefined || (raw as unknown) === "") return null;
    const n = Number(raw);
    if (!Number.isFinite(n) || n < 0 || n > MAX_AMOUNT) {
      problems.push(`The amount for ${what} doesn't look right.`);
      return "bad";
    }
    return round2(n);
  };

  for (const it of items) {
    if (it.key === CUSTOM_ITEM) {
      const label = (it.label ?? "").replace(/\s+/g, " ").trim().slice(0, 80);
      if (!label) {
        if (it.amount != null) problems.push("Say what the off-list line is for.");
        continue;
      }
      const amount = typed(it.amount, label);
      if (amount === "bad") continue;
      lines.push({ key: CUSTOM_ITEM, label, qty: null, unit: "job", rate: null, amount, ...tag });
      if (amount === null) waiting = true;
      else { typedSum += amount; typedAny = true; }
      continue;
    }
    if (dr) {
      // What comes down or goes back up: gutters, downspouts, guards, by the foot at the work's rate.
      const label = byKey.get(it.key)?.label ?? ITEM_LABELS[it.key] ?? it.key;
      const g = ITEM_GROUPS[it.key];
      if (g !== "gutter" && g !== "guard") {
        problems.push(`${label} isn't something the sub detaches or resets.`);
        continue;
      }
      const qty = it.qty === null || it.qty === undefined ? 0 : Number(it.qty);
      if (!Number.isFinite(qty) || qty < 0 || qty > MAX_QTY) {
        problems.push(`The footage for ${label} doesn't look right.`);
        continue;
      }
      if (qty === 0) continue;
      if (!workRate) {
        if (!noRate) problems.push(`The sub's list has no ${work} rate yet.`);
        noRate = true;
        continue;
      }
      const lineLabel = `${work === "detach" ? "Detach" : "Reset"} ${label.toLowerCase()}`;
      if (workRate.price === null) {
        lines.push({ key: it.key, label: lineLabel, qty, unit: "ft", rate: null, amount: null, ...tag });
        continue;
      }
      const amount = round2(qty * workRate.price);
      lines.push({ key: it.key, label: lineLabel, qty, unit: "ft", rate: workRate.price, amount, ...tag });
      listSum += amount;
      listAny = true;
      continue;
    }
    const p = byKey.get(it.key);
    if (!p || ITEM_GROUPS[it.key] === "work") {
      problems.push(`"${it.label ?? ITEM_LABELS[it.key] ?? it.key}" isn't on the sub's price list.`);
      continue;
    }
    if (p.unit === "quote") {
      const amount = typed(it.amount, p.label);
      if (amount === "bad") continue;
      lines.push({ key: p.key, label: p.label, qty: null, unit: "quote", rate: null, amount, ...tag });
      if (amount === null) waiting = true;
      else { typedSum += amount; typedAny = true; }
      continue;
    }
    const qty = it.qty === null || it.qty === undefined ? 0 : Number(it.qty);
    if (!Number.isFinite(qty) || qty < 0 || qty > MAX_QTY) {
      problems.push(`The ${p.unit === "sq" ? "squares" : "footage"} for ${p.label} doesn't look right.`);
      continue;
    }
    if (qty === 0) continue;
    const amount = round2(qty * (p.price ?? 0));
    lines.push({ key: p.key, label: p.label, qty, unit: p.unit, rate: p.price, amount, ...tag });
    listSum += amount;
    listAny = true;
  }
  return {
    lines,
    listTotal: listAny ? round2(listSum) : null,
    knownTotal: listAny || typedAny ? round2(listSum + typedSum) : null,
    waitingOnQuote: waiting,
    problems,
  };
}

/** One price for a visit, or for a pair saved together (a detach and its reset or its new install): every
 *  visit's lines at its own work's rates, and one total. The price sits on the pair's first visit. */
export function priceVisits(visits: Array<{ work: TradeWork; items: PriceInput[] }>, list: PriceListItem[]): Priced {
  const parts = visits.map((v) => priceTrade(v.items, list, v.work));
  const any = (k: "listTotal" | "knownTotal") => parts.some((p) => p[k] !== null);
  return {
    lines: parts.flatMap((p) => p.lines),
    listTotal: any("listTotal") ? round2(parts.reduce((t, p) => t + (p.listTotal ?? 0), 0)) : null,
    knownTotal: any("knownTotal") ? round2(parts.reduce((t, p) => t + (p.knownTotal ?? 0), 0)) : null,
    waitingOnQuote: parts.some((p) => p.waitingOnQuote),
    problems: parts.flatMap((p) => p.problems),
  };
}

export type PriceStatus = "list" | "agreed" | "quote_pending";

/** The price the trade carries. A total Alex typed wins (Jose agreed to something else). Otherwise the lines'
 *  total once every line has an amount, else it waits on the sub's quote. "list" means the price is exactly
 *  the sub's list; "agreed" means a number someone typed. */
export function settlePrice(p: Priced, agreed: number | null | undefined): { agreedTotal: number | null; status: PriceStatus } {
  const allList = p.lines.length > 0 && p.lines.every((l) => l.unit === "ft" || l.unit === "sq");
  if (agreed !== null && agreed !== undefined && Number.isFinite(Number(agreed))) {
    const a = round2(Number(agreed));
    return { agreedTotal: a, status: allList && p.listTotal === a ? "list" : "agreed" };
  }
  if (p.waitingOnQuote || p.knownTotal === null) return { agreedTotal: null, status: "quote_pending" };
  return { agreedTotal: p.knownTotal, status: allList ? "list" : "agreed" };
}

// ── The price to the customer and the margin ──
// Greg, Oct 29, 2026: "We need to track the other trades with a 30% margin. Whatever is lacking from that
// margin needs to be flagged for review by Chad" (on the Commission board). Margin is the share of the
// customer's price Bardin keeps: at 30%, JAC's $6.25 a foot of gutters is $8.93 to the customer.

export type BilledTo = "insurance" | "homeowner" | "bardin";
export const BILLED_TOS: BilledTo[] = ["insurance", "homeowner", "bardin"];
export function isBilledTo(v: unknown): v is BilledTo {
  return typeof v === "string" && (BILLED_TOS as string[]).includes(v);
}
/** Chad's settlement (Greg, Oct 29: "Chad is just reviewing and removing the loss from the commission or
 *  overriding and allowing with no recourse"): allowed = no recourse; deducted = the shortfall joins the deal's
 *  non-collect bucket (Greg, Oct 29: "let it blend into non-collect revenue"; the first $500 of the bucket is
 *  forgiven, what's over comes off the commission split RM / TL / ASM). */
export type MarginReview = "none" | "needs_review" | "allowed" | "deducted";

/** The share of the customer's price that covers the sub's cost at the target margin: 1 - pct/100, the target
 *  clamped to 0..95 (30 → 0.7). One clamp for every floor number here and in trade_sync_deal's SQL. */
export function marginKeep(targetPct: number): number {
  return 1 - Math.min(Math.max(Number(targetPct) || 0, 0), 95) / 100;
}

/** The customer price that leaves the target margin on a cost: cost / (1 - pct/100), to the cent. This is the
 *  floor (Greg, Oct 29: "the 30% floor"), the minimum to charge, where the commission starts. */
export function priceAtMargin(cost: number, targetPct: number): number {
  if (!Number.isFinite(cost) || cost <= 0) return 0;
  return round2(cost / marginKeep(targetPct));
}

/** The floor on a cost (priceAtMargin by its other name), or null while the cost is unknown (a quote pending).
 *  A line's minimum and the total minimum both come from here on the sub's amounts: a cent-rounded floor rate
 *  times the footage drifts (210 ft × $8.93 = $1,875.30, the floor of $1,312.50 is $1,875.00). */
export function floorOf(cost: number | null | undefined, targetPct: number): number | null {
  if (cost === null || cost === undefined || !Number.isFinite(cost)) return null;
  return priceAtMargin(cost, targetPct);
}

/** The minimum to charge per foot (or square) on a sub's rate, unrounded: a label to format to the cent, never
 *  a number to multiply (see floorOf). $6.25 a foot of gutters → $8.93; $2.50 to detach → $3.57. */
export function floorRate(rate: number | null | undefined, targetPct: number): number | null {
  if (rate === null || rate === undefined || !Number.isFinite(rate)) return null;
  return rate / marginKeep(targetPct);
}

/** A sub's list with every price at its floor rate, for display only. Never price a trade with it: the floor of
 *  a floor would be the cost / 0.49. */
export function floorList(list: PriceListItem[], targetPct: number): PriceListItem[] {
  return list.map((p) => ({ ...p, price: floorRate(p.price, targetPct) }));
}

/** The customer's price against the floor, to the cent: positive = upside above it (paid as commission on a
 *  homeowner-billed trade), negative = short of it (minus marginShortfall). Null without both numbers. */
export function upsideAbove(cost: number | null | undefined, price: number | null | undefined, targetPct: number): number | null {
  if (cost === null || cost === undefined || price === null || price === undefined) return null;
  if (!Number.isFinite(cost) || !Number.isFinite(price)) return null;
  return round2(price - priceAtMargin(cost, targetPct));
}

/** What one trade's price adds to the deal's homeowner-billed trade money (⚠ the per-trade arithmetic of
 *  trade_sync_deal, migration 20261029f): a homeowner-billed trade with a customer price adds that price to
 *  deals.trade_ho_total and what's above its floor to deals.trade_upside; a trade waiting on the sub's price
 *  (cost null) counts its whole price as the floor, so no upside until it's priced. Anything else adds nothing. */
export function tradeHoShare(billedTo: string | null | undefined, customer: number | null | undefined, cost: number | null | undefined, targetPct: number): { total: number; upside: number } {
  if (billedTo !== "homeowner" || customer === null || customer === undefined || !Number.isFinite(customer)) return { total: 0, upside: 0 };
  const floor = cost === null || cost === undefined || !Number.isFinite(cost) ? customer : Math.min(customer, priceAtMargin(cost, targetPct));
  return { total: round2(customer), upside: round2(Math.max(0, customer - floor)) };
}

/** What a job counts toward the deal's homeowner-billed trade money as it stands (⚠ the per-trade arithmetic of
 *  trade_sync_deal, migration 20261029g): a homeowner-billed priced job counts at its price once its add-on is
 *  not needed or signed; while it waits on a new signature it counts at the price the homeowner last agreed to (the
 *  add-on's amount: a signature, or the install agreement's price carried by the trigger), else nothing (review of the
 *  release, Oct 30: a signed job repriced dropped off the invoice, and a balance at $0 sent "paid in full"). */
export function countedHoShare(i: {
  billedTo: string | null | undefined; customer: number | null | undefined; cost: number | null | undefined; targetPct: number;
  addendumStatus: string | null | undefined; addendumAmount: number | null | undefined;
}): { total: number; upside: number } {
  if (i.addendumStatus === "needs_signature" || i.addendumStatus === "sent") {
    if (i.customer === null || i.customer === undefined) return { total: 0, upside: 0 };
    return tradeHoShare(i.billedTo, i.addendumAmount, i.cost, i.targetPct);
  }
  return tradeHoShare(i.billedTo, i.customer, i.cost, i.targetPct);
}

/** A pair's price to the customer for the visit that goes ahead when the other is cancelled (review of the release,
 *  Oct 30: the reset alone kept the price of the detach and reset, which went on the homeowner's invoice and paid
 *  upside for cancelled work). The pair's price scaled by the share of the sub's cost the surviving visit carries, to
 *  the cent: at the floor it lands on the survivor's floor, above it the same margin. Null when it can't be worked
 *  out (no price, or either cost unknown): the price then stays as it was and Chad is asked to confirm it. */
export function pairSurvivorCustomer(customer: number | null | undefined, pairCost: number | null | undefined, survivorCost: number | null | undefined): number | null {
  if (customer === null || customer === undefined || !Number.isFinite(customer)) return null;
  if (pairCost === null || pairCost === undefined || !Number.isFinite(pairCost) || pairCost <= 0) return null;
  if (survivorCost === null || survivorCost === undefined || !Number.isFinite(survivorCost) || survivorCost < 0) return null;
  return round2(customer * Math.min(1, survivorCost / pairCost));
}

/** The reason a trade short of the floor goes to Chad: "$342.86 short of the 30% floor." */
export function shortOfFloorText(short: number, targetPct: number): string {
  return `${fmtMoneyPlain(short)} short of the ${fmtPct(targetPct)} floor.`;
}

const LEGACY_MARGIN_REASON = /The margin is -?[\d.,]+%, under the [\d.]+% target\.\s*/;
/** A review reason as reps read it (A1, Greg, Oct 29: no margin % in front of them). A row priced before Oct 29 still
 *  says "The margin is 22.5%, under the 30% target."; that sentence becomes the shortfall in dollars when the floor
 *  and the price are known, else it is left out. Null when nothing is left. */
export function repMarginReason(reason: string | null | undefined, floor: number | null | undefined, price: number | null | undefined, targetPct: number): string | null {
  const r = typeof reason === "string" ? reason : "";
  if (!r.trim()) return null;
  if (!LEGACY_MARGIN_REASON.test(r)) return r;
  const short = floor !== null && floor !== undefined && Number.isFinite(floor)
    ? round2(Math.max(0, floor - (price !== null && price !== undefined && Number.isFinite(price) ? Math.max(0, price) : 0)))
    : 0;
  const out = r.replace(new RegExp(LEGACY_MARGIN_REASON.source, "g"), () => (short > 0 ? `${shortOfFloorText(short, targetPct)} ` : "")).replace(/\s+/g, " ").trim();
  return out || null;
}

/** Bardin's margin on a trade as a share of the customer's price, to a tenth of a percent; null without both
 *  numbers or when nothing is charged. */
export function marginPct(cost: number | null | undefined, price: number | null | undefined): number | null {
  if (cost === null || cost === undefined || price === null || price === undefined) return null;
  if (!Number.isFinite(cost) || !Number.isFinite(price) || price <= 0) return null;
  return Math.round(((price - cost) / price) * 1000) / 10;
}

/** What's lacking from the margin (Greg, Oct 29: "the lack, not actual"): the gap between the price at the
 *  target margin and the price charged, never below zero. Nothing charged means the whole target price. Null
 *  while the sub's price is unknown. */
export function marginShortfall(cost: number | null | undefined, price: number | null | undefined, targetPct: number): number | null {
  if (cost === null || cost === undefined || !Number.isFinite(cost) || cost <= 0) return null;
  const target = priceAtMargin(cost, targetPct);
  const charged = price === null || price === undefined || !Number.isFinite(price) ? 0 : Math.max(0, price);
  return round2(Math.max(0, target - charged));
}

/** "30%" / "22.5%" */
export function fmtPct(n: number): string {
  return `${Number.isInteger(n) ? n : n.toFixed(1)}%`;
}

export interface MarginCheckInput {
  /** The sub's price (agreed, else the list), or null while it waits on a quote. */
  cost: number | null;
  /** The price to the customer, or null when none was typed yet. */
  price: number | null;
  billedTo: BilledTo | null;
  targetPct: number;
  /** The work of each visit the price covers: one, or a detach and its reset / new install. */
  works: TradeWork[];
  /** The deal's "items not performing" says the insurance scope paid for the detach and reset. */
  scopeHadIt: boolean;
  /** What the parsed scope pays for the gutter detach and reset, when the parser has run. */
  scopeRcv?: number | null;
}

/** Whether a trade needs Chad: short of the floor (the price at the target margin), nothing charged, done at
 *  Bardin's cost, or a detach or reset billed outside the claim after the insurance scope paid for it (Greg,
 *  Oct 29: "He removes that from scope as not completing. Then adds as retail for cheaper."). A detach or reset
 *  billed to insurance at what the parsed scope pays for it skips Chad whatever the margin (Greg: "skip Chad when
 *  billed at the scope amount"); billed below it, he sees it. That scope rule is for a job that is only a detach
 *  and reset: a detach + new install's one price also buys the new gutters, which the scope's D&R line never
 *  paid for (delta review of PR #76, Oct 29, 2026). "unknown" while the sub's price is missing and nothing else
 *  is wrong; a known cost with no customer price on a homeowner- or insurance-billed job needs Chad.
 *  Oct 29 (Greg: "Just show the 30% floor and do math from there"): short means a cent under the floor, the
 *  same test the sheet and Chad's Deduct amount use (marginShortfall), and the reason says it in dollars
 *  ("$342.86 short of the 30% floor."); a margin rounded to a tenth could read 30% a dollar under the floor. */
export function marginCheck(i: MarginCheckInput): { status: "ok" | "needs_review" | "unknown"; margin: number | null; reasons: string[]; atScope: boolean } {
  const reasons: string[] = [];
  const margin = marginPct(i.cost, i.price);
  const isDr = (w: TradeWork) => w === "detach" || w === "reset";
  const anyDr = i.works.some(isDr);
  const allDr = i.works.length > 0 && i.works.every(isDr);
  const scopeRcv = i.scopeRcv ?? null;
  const atScope = allDr && i.billedTo === "insurance" && scopeRcv !== null && scopeRcv > 0 && i.price !== null && i.price >= scopeRcv;
  if (i.billedTo === "bardin") reasons.push("Bardin pays the sub; nothing is billed to anyone.");
  if (anyDr && i.scopeHadIt && i.billedTo !== "insurance") {
    reasons.push("The insurance scope paid for this detach and reset; the deal marks it not performing and it's billed outside the claim.");
  }
  if (i.billedTo !== "bardin" && !atScope) {
    if (i.price !== null && i.price <= 0) reasons.push("Nothing is charged for it.");
    else if (i.cost === null) return { status: reasons.length ? "needs_review" : "unknown", margin, reasons, atScope };
    else if (i.price === null) reasons.push(i.billedTo ? "No price to the customer was entered yet." : "Nobody is billed for it yet.");
    else {
      const short = marginShortfall(i.cost, i.price, i.targetPct);
      if (short !== null && short > 0) {
        reasons.push(allDr && i.billedTo === "insurance" && scopeRcv !== null && scopeRcv > 0
          ? `Billed ${fmtMoneyPlain(i.price)} to insurance, under the ${fmtMoneyPlain(scopeRcv)} the scope pays for it; ${shortOfFloorText(short, i.targetPct)}`
          : shortOfFloorText(short, i.targetPct));
      }
    }
  }
  return { status: reasons.length ? "needs_review" : "ok", margin, reasons, atScope };
}

/** margin_pct as the table stores it, numeric(6,1): a customer price typed as "1" for a $1,250 job is a margin
 *  of -124,900%, which overflowed the column and lost the whole price row (delta review, Oct 29, 2026). */
export function marginPctForRow(margin: number | null): number | null {
  if (margin === null || !Number.isFinite(margin)) return null;
  return Math.min(99999.9, Math.max(-99999.9, margin));
}

/** "$412.50" (the function has no Intl worries; two decimals, thousands commas). */
function fmtMoneyPlain(n: number): string {
  const [whole, cents] = n.toFixed(2).split(".");
  return `$${whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}.${cents}`;
}

const SCOPE_DETACH_RESET = /gutter|guard|detach|reset|\bd\s*(&|and)\s*r\b/i;

/** A gutter detach-and-reset line in the parsed insurance scope, with what the carrier pays for it. */
export interface ScopeLine { description: string; rcv: number | null }

/** The gutter detach-and-reset lines in deals.parsed_scope (the Scope card's shape today, and the one the
 *  scope parser Greg is adding writes: { line_items: [{ description, rcv, ... }] }). Their RCV is what
 *  insurance pays for the work, so an insurance-billed detach or reset starts from it. */
export function scopeDetachResetLines(parsedScope: unknown): ScopeLine[] {
  const items = parsedScope && typeof parsedScope === "object" && Array.isArray((parsedScope as { line_items?: unknown }).line_items)
    ? ((parsedScope as { line_items: unknown[] }).line_items)
    : [];
  const out: ScopeLine[] = [];
  for (const raw of items) {
    if (!raw || typeof raw !== "object") continue;
    const it = raw as { description?: unknown; rcv?: unknown };
    const description = typeof it.description === "string" ? it.description.replace(/\s+/g, " ").trim() : "";
    if (!/gutter|downspout|guard/i.test(description)) continue;
    // Detach / reset / D&R only: "Remove & Replace - Gutters" is the carrier buying new gutters, not paying to
    // take them down and put them back (delta review of PR #76, Oct 29, 2026: it inflated what the scope pays).
    if (!/detach|reset|\bd\s*(&|and)\s*r\b/i.test(description)) continue;
    const n = it.rcv === null || it.rcv === undefined || it.rcv === "" ? NaN : Number(it.rcv);
    out.push({ description, rcv: Number.isFinite(n) ? round2(n) : null });
  }
  return out;
}

/** What the scope pays for the gutter detach and reset, or null when no line carries an amount. */
export function scopeDetachResetRcv(lines: ScopeLine[]): number | null {
  const amounts = lines.map((l) => l.rcv).filter((n): n is number => n !== null);
  return amounts.length ? round2(amounts.reduce((t, n) => t + n, 0)) : null;
}

/** The insurance scope paid for the gutter detach and reset: the parsed scope has the line, or the deal's
 *  "items not performing" names it (the free-text field, until the scope parser fills line_items). */
export function scopeHadDetachReset(itemsNotPerformingLines: string | null | undefined, parsedScope?: unknown): boolean {
  return SCOPE_DETACH_RESET.test(itemsNotPerformingLines ?? "") || scopeDetachResetLines(parsedScope).length > 0;
}

/** "210 ft", "11.5 squares" */
export function fmtQty(qty: number | null, unit: PriceUnit | "job"): string {
  if (qty === null) return "";
  const n = Number.isInteger(qty) ? String(qty) : String(round2(qty));
  if (unit === "ft") return `${n} ft`;
  if (unit === "sq") return `${n} ${qty === 1 ? "square" : "squares"}`;
  return n;
}

// ── Words ──

/** "gutters", "gutters and downspouts", "gutters, downspouts and gutter guards" (off-list lines left out).
 *  With no item that has a word, soffit / fascia work is "fascia and soffit" (its lines are off-list when the
 *  sub was typed on the sheet, and the work itself says what it is); any other work guesses "gutters", which is
 *  why textCanNameWork keeps those jobs out of the texts. */
export function itemsPhrase(items: TradeItem[], work?: TradeWork): string {
  const words: string[] = [];
  for (const it of items) {
    const w = ITEM_WORDS[it.key];
    if (w && !words.includes(w)) words.push(w);
  }
  if (words.length === 0) return work === "soffit_fascia" ? "fascia and soffit" : "gutters";
  if (words.length === 1) return words[0];
  return `${words.slice(0, -1).join(", ")} and ${words[words.length - 1]}`;
}

// ── The homeowner's line (Greg, Oct 29: "the homeowner agreement picks it up from there") ──
// A homeowner-billed trade is one line on the install agreement and the invoice: trade_item_prices.customer_label,
// written by build-calendar on every price write, gathered into deals.trade_ho_lines by trade_sync_deal. Plain
// words of the work and its footage; never what was agreed with the sub (scope_notes), never a sub figure.

/** What a trade's customer line can read from trade_items.details. */
export interface CustomerLabelDetails { gutter_size?: unknown; other_label?: unknown }

const clip = (s: unknown): string => (typeof s === "string" ? s.replace(/\s+/g, " ").trim() : "");

/** The footage that leads a customer line: gutters and downspouts together (separate runs, both installed),
 *  else the guards, else fascia or soffit (one figure: they run along the same eaves), else the squares of
 *  siding. "" when nothing on the line has footage. */
function customerQty(items: TradeItem[]): string {
  const ft = (g: ItemGroup) => items.filter((i) => ITEM_GROUPS[i.key] === g && (i.qty ?? 0) > 0);
  const sum = (xs: TradeItem[]) => round2(xs.reduce((t, i) => t + (i.qty ?? 0), 0));
  const gutter = sum(ft("gutter"));
  if (gutter > 0) return fmtQty(gutter, "ft");
  const guard = sum(ft("guard"));
  if (guard > 0) return fmtQty(guard, "ft");
  const trim = ft("trim").reduce((m, i) => Math.max(m, i.qty ?? 0), 0);
  if (trim > 0) return fmtQty(trim, "ft");
  const siding = sum(items.filter((i) => i.key === "siding" && (i.qty ?? 0) > 0));
  return siding > 0 ? fmtQty(siding, "sq") : "";
}

/** The line the homeowner reads for a trade: "Gutters: detach and reset, 199 ft", "New 6" gutters and
 *  downspouts, 347 ft", "Gutter guards: new install, 120 ft", "Soffit and fascia, 80 ft", "Gutter service".
 *  `works` are the visits the one price covers (a detach and its reset, a detach and the new install after the
 *  roof, or one visit); `items` are the priced lines (a line's `work` says which visit it's on). A pair reads as
 *  the visit the homeowner is buying: the new install when there is one (the old gutters coming down are part of
 *  it), else the detach, whose footage its reset shares. An off-list line is added in Alex's words ("Touch-up
 *  paint"), and is the whole line when nothing else on it has a word (a sub typed on the sheet: "Detach and
 *  reset guards"). Never a price, never what was agreed with the sub. At most 200 characters. */
export function tradeCustomerLabel(works: TradeWork[], items: Array<TradeItem & { work?: TradeWork }>, details?: CustomerLabelDetails | null): string {
  const main: TradeWork = works.includes("new_install") ? "new_install" : works.includes("detach") ? "detach" : works[0] ?? "other";
  const own = items.filter((i) => i.key !== CUSTOM_ITEM && (!i.work || i.work === main));
  const extras: string[] = [];
  for (const i of items) {
    const l = i.key === CUSTOM_ITEM ? clip(i.label) : "";
    if (l && !extras.some((e) => e.toLowerCase() === l.toLowerCase())) extras.push(l);
  }
  const named = own.some((i) => ITEM_WORDS[i.key] !== undefined);
  let phrase = named ? itemsPhrase(own, main) : "";
  if (phrase === "fascia and soffit") phrase = "soffit and fascia";
  const Phrase = phrase.charAt(0).toUpperCase() + phrase.slice(1);
  const qty = named ? customerQty(own) : "";
  const ft = qty ? `, ${qty}` : "";
  const hasD = works.includes("detach"), hasR = works.includes("reset");
  const dr = works.length > 0 && works.every((w) => w === "detach" || w === "reset");
  const size = clip(details?.gutter_size);
  const otherLabel = clip(details?.other_label);
  let base: string;
  if (main === "other") base = otherLabel ? `${otherLabel}${ft}` : named ? `${Phrase}${ft}` : "";
  else if (!named) base = "";
  else if (dr) base = `${Phrase}: ${hasD && hasR ? "detach and reset" : hasD ? "detach" : "reset"}${ft}`;
  else if (main === "new_install") {
    base = /^(gutters|downspouts)\b/.test(phrase) ? `New ${size ? `${size} ` : ""}${phrase}${ft}` : `${Phrase}: new install${ft}`;
  } else if (main === "service") {
    base = phrase === "gutters" ? `Gutter service${ft}` : phrase === "interior repair" ? `Interior repair${ft}` : `${Phrase}: service${ft}`;
  } else base = `${Phrase}${ft}`;   // soffit / fascia: "Soffit and fascia, 80 ft"
  // Nothing on it has a word: Alex's off-list lines say what it is; else the work in plain words.
  if (!base) {
    if (extras.length) return extras.join("; ").slice(0, 200);
    base = main === "detach" ? "Detach before the roof" : main === "reset" ? "Reset after the roof" : main === "new_install" ? "New install"
      : main === "service" ? "Service call" : main === "soffit_fascia" ? "Soffit and fascia" : "Other work";
  }
  return (extras.length ? `${base}; ${extras.join("; ")}` : base).slice(0, 200);
}

/** The words a customer line reads when its price row has no customer_label (a row priced before Oct 29). ⚠ Mirror
 *  of the CASE in SQL trade_sync_deal (migration 20261029f) and trade_addendum_label (20261029g). */
const WORK_PLAIN_WORDS: Record<TradeWork, string> = {
  detach: "Gutters: detach", reset: "Gutters: reset", new_install: "New gutters", service: "Gutter service",
  soffit_fascia: "Soffit and fascia", other: "Trade work",
};

/** The customer line as the agreement, the invoice and the add-on read it: the stored label, else the plain words
 *  of the work (the lead visit's). */
export function effectiveCustomerLabel(label: string | null | undefined, work: TradeWork | string | null | undefined): string {
  const l = typeof label === "string" ? label.trim() : "";
  if (l) return l;
  return isTradeWork(work) ? WORK_PLAIN_WORDS[work] : "Trade work";
}

// ── The trade add-on (Greg, Oct 29, 2026) ──
// "if a trade is added after the homeowner has signed, the addendum is made. could we treat that similar to the color
// confirmation text ... sign and schedule button are below. sign brings up addendum, work and price, homeowner signs
// and is sent a link to open and sign. once signed, the schedule button is available ... all that is if we are doing
// roof not a stand alone retail job." The status lives on trade_item_prices.addendum_status (migration 20261029g,
// whose BEFORE trigger is the authority); these mirror it so the sheet can say what will happen and build-calendar
// can refuse a dated save before it writes anything.

export type AddendumStatus = "not_needed" | "needs_signature" | "sent" | "signed";
export const ADDENDUM_STATUSES: AddendumStatus[] = ["not_needed", "needs_signature", "sent", "signed"];
export function isAddendumStatus(v: unknown): v is AddendumStatus {
  return typeof v === "string" && (ADDENDUM_STATUSES as string[]).includes(v);
}
/** Waiting on the homeowner: not sent yet, or sent and not signed. A job in either state isn't scheduled or billed. */
export function isAddendumOpen(s: unknown): s is "needs_signature" | "sent" {
  return s === "needs_signature" || s === "sent";
}

/** The homeowner signed the install agreement (⚠ the SQL trigger's test: install_agreement_signed or agreement_status
 *  'signed', as trade_context words it). */
export function dealAgreementSigned(d: { install_agreement_signed?: boolean | null; agreement_status?: string | null }): boolean {
  return d.install_agreement_signed === true || d.agreement_status === "signed";
}

/** A roof job: an insurance scope (rcv_total > 0) or a retail roof (retail_squares > 0). A deal with neither is a
 *  stand-alone retail trade job, whose own contract covers the trade (Greg: "if we are doing roof not a stand alone
 *  retail job"). */
export function dealIsRoofJob(d: { rcv_total?: number | string | null; retail_squares?: number | string | null }): boolean {
  const n = (v: number | string | null | undefined) => (v === null || v === undefined || v === "" ? 0 : Number(v));
  return n(d.rcv_total) > 0 || n(d.retail_squares) > 0;
}

/** Rule 1: the job needs the homeowner's add-on signature. A homeowner-billed trade with a price to the homeowner,
 *  on a roof job whose install agreement is signed. ⚠ Mirror of trade_item_prices_addendum (migration 20261029g). */
export function addendumApplies(i: { billedTo: string | null | undefined; customerTotal: number | null | undefined; agreementSigned: boolean; roofJob: boolean }): boolean {
  return i.billedTo === "homeowner" && i.customerTotal !== null && i.customerTotal !== undefined && Number.isFinite(i.customerTotal)
    && i.agreementSigned && i.roofJob;
}

export interface AddendumStatusInput {
  /** addendumApplies after this write. */
  applies: boolean;
  /** The price to the homeowner and the customer line after this write (effectiveCustomerLabel). */
  customerTotal: number | null;
  label: string;
  /** The price row before this write; null for a new job. `payerHomeowner`: the job was billed to the homeowner
   *  before this write (a payer that becomes the homeowner asks for a signature, as the trade_items trigger does).
   *  `label` null: the row had no customer_label stored (priced before Oct 29, or by a build-calendar deployed
   *  before it wrote one), so its words compare equal to the new ones (review of the release, Oct 30: the first
   *  save by the new function read the fallback "Gutters: detach" as a change and asked a signed roof job for a
   *  signature). */
  prev: { status: AddendumStatus; customerTotal: number | null; label: string | null; payerHomeowner: boolean } | null;
  /** The price and words on file in the add-on's fields: a signature (`at` set), kept as history through a reprice
   *  or a payer change; or (`at` null) what the homeowner agreed to on the install agreement, carried there when a
   *  job that was on it changed after the signing (the trigger, 20261029g). */
  signed: { amount: number | null; label: string | null; at: string | null } | null;
}

/** Rule 2: the add-on's status after a save. Not needed when rule 1 doesn't hold; signed when the homeowner already
 *  signed for this amount and these words; otherwise a signature is needed, except that a save that changes nothing
 *  the homeowner signs for keeps the status (a sent add-on stays sent; a job that was on the agreement when it was
 *  signed stays not needed). ⚠ Mirror of the trigger trade_item_prices_addendum (migration 20261029g). */
export function addendumStatusAfter(i: AddendumStatusInput): AddendumStatus {
  if (!i.applies) return "not_needed";
  const s = i.signed;
  const same = (a: number | null | undefined, b: number | null | undefined) =>
    a !== null && a !== undefined && b !== null && b !== undefined && round2(Number(a)) === round2(Number(b));
  if (s && s.at && same(s.amount, i.customerTotal) && (s.label ?? null) === i.label) return "signed";
  const p = i.prev;
  // Back to the price and the words that were on the signed install agreement (carried, never signed as an add-on).
  if (p && s && !s.at && same(s.amount, i.customerTotal) && (s.label ?? null) === i.label) return "not_needed";
  if (!p || !p.payerHomeowner) return "needs_signature";
  const moneySame = (p.customerTotal === null && i.customerTotal === null) || same(p.customerTotal, i.customerTotal);
  if (!moneySame || (p.label !== null && p.label !== i.label)) return "needs_signature";
  if (p.status === "needs_signature" || p.status === "sent") return p.status;
  if (p.status === "not_needed") return "not_needed";
  return "needs_signature";
}

/** "$1,000.00": the add-on's price as the text and the page say it. */
export function fmtAddendumAmount(n: number | null | undefined): string {
  return fmtMoneyPlain(n === null || n === undefined || !Number.isFinite(n) ? 0 : round2(n));
}

/** The first name the add-on text opens with: exactly as ho-portal-notify's colors text works it out (the first word
 *  of the name, else "there"), so the two links read alike. */
export function addendumFirstName(homeownerName: string | null | undefined): string {
  return (homeownerName || "").split(" ")[0] || "there";
}

/** The /colors link token, built exactly as ho-portal-notify (colorsToken) and the signing page build it:
 *  base64("<deal id>:<last word of the name, lower case>:<street number>"). */
export function portalLinkToken(dealId: string, homeownerName: string | null | undefined, street: string | null | undefined): string {
  const lastName = (homeownerName || "").split(" ").pop() ?? "";
  const streetNum = (street || "").match(/^(\d+)/)?.[1] ?? "";
  const raw = `${dealId}:${lastName.toLowerCase()}:${streetNum}`;
  try {
    return btoa(raw);
  } catch {
    return btoa(raw.replace(/[^\x00-\xFF]/g, ""));
  }
}

/** The homeowner's link to the add-on, or null when the portal couldn't open it (useHomeownerAuth needs a last name
 *  and a street number in the token). */
export function tradeAddendumLink(dealId: string, homeownerName: string | null | undefined, street: string | null | undefined, tradeId: string): string | null {
  const lastName = (homeownerName || "").split(" ").pop() ?? "";
  const streetNum = (street || "").match(/^(\d+)/)?.[1] ?? "";
  if (!dealId || !lastName || !streetNum || !tradeId) return null;
  return `https://portal.bardinroofing.com/trade-addendum?token=${encodeURIComponent(portalLinkToken(dealId, homeownerName, street))}&trade=${encodeURIComponent(tradeId)}`;
}

/** The homeowner's text, the wording Greg approved ("yes", Oct 29, 2026), word for word, from the customer line. Any
 *  change needs his OK. */
export function tradeAddendumText(i: { homeownerName: string | null | undefined; label: string; amount: number | null | undefined; link: string }): string {
  return `Hi ${addendumFirstName(i.homeownerName)} — one more quick step. Tap to review and sign the add-on to your roof agreement (${i.label}, ${fmtAddendumAmount(i.amount)}):\n\n${i.link}\n\nQuestions? Reply here or call (470) 823-8816. — Bardin Roofing`;
}

/** The add-on's number: TAA-<claim or RETAIL>-<LAST NAME>-<n>, like the color addendum's CCA-<claim>-<LAST NAME>. */
export function tradeAddendumNumber(claimNumber: string | null | undefined, homeownerName: string | null | undefined, n: number): string {
  const claim = (claimNumber ?? "").trim() || "RETAIL";
  const lastName = (homeownerName || "").split(" ").pop()?.toUpperCase() || "";
  return `TAA-${claim}-${lastName}-${Math.max(1, Math.floor(n) || 1)}`;
}

/** "gutter crew" for gutters, downspouts and guards; "crew" for anything else. No list item at all reads as the
 *  gutter crew, except on soffit / fascia work, which is the crew whatever its lines say. */
export function crewWord(items: TradeItem[], work?: TradeWork): "gutter crew" | "crew" {
  const groups = items.map((i) => itemGroup(i.key)).filter((g) => g !== "custom");
  if (groups.length === 0) return work === "soffit_fascia" ? "crew" : "gutter crew";
  return groups.some((g) => g === "gutter" || g === "guard") ? "gutter crew" : "crew";
}

/** Whether the texts Greg approved (plan 3e) can say what the visit is for: a list item has a word for it.
 *  An Other job, or a visit whose only line is off-list ("Soffit repair", touch-up paint, "Detach and reset
 *  guards" typed as one line), has no approved wording, so no text goes out for it and the sheet says so
 *  (pre-deploy review, Oct 29, 2026: it read "our gutter crew ... your gutters"). A detach or reset with no
 *  items (one adopted from a hand-made event, before its footage is typed) is the same: the text would guess
 *  "gutters" for a guards job (delta review of PR #76, Oct 29). Soffit / fascia work has its own words whatever
 *  its lines (itemsPhrase: "fascia and soffit"), so a soffit / fascia job with a sub typed on the sheet, off-list
 *  lines only, still gets the existing generic sentence (review of the Oct 29 branch). */
export function textCanNameWork(work: TradeWork, items: TradeItem[]): boolean {
  return work === "soffit_fascia" || items.some((i) => ITEM_WORDS[i.key] !== undefined);
}

/** A county-record name in capitals ("GUTIERREZ HOLLY") reads in title case on the event. */
function displayName(name: string | null | undefined): string {
  const s = (name ?? "").trim().replace(/\s+/g, " ");
  if (!s) return "(no name)";
  if (s !== s.toUpperCase() || !/[A-Z]/.test(s)) return s;
  return s.toLowerCase().replace(/(^|[\s'-])(\p{L})/gu, (_m, sep: string, ch: string) => sep + ch.toUpperCase());
}

/** The footage that leads the title, as Robert writes it ("230ft / ..."): gutters, else guards. */
function leadFeet(items: TradeItem[]): number | null {
  const ft = (keys: (k: string) => boolean) =>
    items.filter((i) => keys(i.key) && i.qty !== null && i.qty > 0).reduce((m, i) => Math.max(m, i.qty ?? 0), 0);
  const gutters = ft((k) => k === "gutters");
  const guards = ft((k) => ITEM_GROUPS[k] === "guard");
  const n = gutters || guards;
  return n > 0 ? Math.round(n) : null;
}

export interface TradeTitleInput {
  homeowner: string | null;
  work: TradeWork;
  items: TradeItem[];
  otherLabel?: string | null;
}

/** The Google event's title, in the style of Robert's hand-made events: "230ft / Holly Gutierrez detach
 *  gutter guards". A soffit / fascia job reads "Holly Gutierrez fascia and soffit" from its items (no footage
 *  lead: that is for gutters and guards), or "fascia and soffit" when its only lines are off-list. Any other
 *  work with no item that has a word (a sub typed on the sheet, off-list lines only; a detach adopted from a
 *  hand-made event before its footage is typed) is the verb alone, then the first off-list label: "Jones new
 *  install: Replace 40 ft of siding", never "Jones install new gutters" for an "Acme Siding" trade (review of
 *  the Oct 29 branch). Titles are Robert's style, not homeowner wording. */
export function tradeEventTitle(i: TradeTitleInput): string {
  const what = itemsPhrase(i.items, i.work);
  const named = textCanNameWork(i.work, i.items);
  const firstLine = i.items.find((it) => it.key === CUSTOM_ITEM && (it.label ?? "").trim())?.label?.trim();
  const bare = (verb: string) => (firstLine ? `${verb}: ${firstLine}` : verb);
  const work =
    i.work === "detach" ? (named ? `detach ${what}` : bare("detach"))
      : i.work === "reset" ? (named ? `reset ${what}` : bare("reset"))
        : i.work === "new_install" ? (named ? `install new ${what}` : bare("new install"))
          : i.work === "service" ? (named ? `${what} service` : bare("service"))
            : i.work === "soffit_fascia" ? what
              : (i.otherLabel ?? "").trim() || what;
  const feet = leadFeet(i.items);
  return `${feet ? `${feet}ft / ` : ""}${displayName(i.homeowner)} ${work}`.slice(0, 300);
}

/** The work a hand-made Other Trades event reads as, for trade_link_existing: its title and description in
 *  lower case. A reset or a detach says so. Fascia or soffit is soffit / fascia work unless a gutter word is
 *  there too ("install new gutters, wrap fascia" is a gutter install; review of the Oct 29 branch), in which
 *  case install / new makes it a new install; anything else is a service call. */
export function linkedEventWork(text: string): TradeWork {
  const t = text.toLowerCase();
  if (/\breset\b/.test(t)) return "reset";
  if (/\bdetach\b/.test(t)) return "detach";
  if (/\b(fascia|soffit)\b/.test(t) && !/\b(gutters?|guards?|downspouts?)\b/.test(t)) return "soffit_fascia";
  return /\binstall\b|\bnew\b/.test(t) ? "new_install" : "service";
}

/** The title a move or an edit sends. A visit linked from one of Robert's hand-made events on launch
 *  (trade_link_existing) has no items until Alex types them, so the composed title would read "Holly
 *  Gutierrez reset gutters" and lose his "230ft / Holly Gutierrez reset guards". Until then it keeps the title
 *  he typed, any DATE CHANGE / CALLED OFF marker stripped (the move puts one back). Pre-deploy review, Oct 29. */
export function tradeTitleToSend(row: { calendar_source: string | null; items: TradeItem[] | null }, composed: string, handTyped: string | null | undefined): string {
  const typed = (handTyped ?? "").trim();
  if (row.calendar_source === "adopted" && !(row.items ?? []).length && typed) return stripStatusPrefix(typed).slice(0, 300);
  return composed;
}

/** The title an Edit (or a Move that only changes the arrival) writes back onto the event: the fresh one,
 *  keeping the DATE CHANGE a Move put there (pre-deploy review, Oct 29, 2026: the rebuilt title was wiping the
 *  marker Robert and Jose read on Other Trades). A Move to another day still uses rescheduledSummary. */
export function keepDateChange(current: string | null | undefined, fresh: string): string {
  return /DATE CHANGE/i.test(current ?? "") ? rescheduledSummary(fresh) : fresh;
}

// ── Against the roof ──

/** Why a trade's day doesn't fit the roof, or null when it does (or there's nothing to compare). */
export function roofFitProblem(timing: TradeTiming, date: string | null, roofStart: string | null, roofEnd: string | null): string | null {
  if (!date || !roofStart || !isDateOnly(date) || !isDateOnly(roofStart)) return null;
  if (timing === "before_build" && date >= roofStart) {
    return `The roof starts ${fmtShort(roofStart)}, so ${fmtShort(date)} is too late for work that goes before it.`;
  }
  const last = normalizeBuildEnd(roofStart, roofEnd) ?? roofStart;
  if (timing === "after_build" && date <= last) {
    return `The roof ${last === roofStart ? "is" : "runs through"} ${fmtShort(last)}, so ${fmtShort(date)} is too early for work that goes after it.`;
  }
  return null;
}

/** The build day a visit hangs off: the first day for work before the roof, the last day for work after it.
 *  A roof change that keeps that day changes nothing for the visit (pre-deploy review, Oct 29, 2026: a one-day
 *  build stretched to two days was re-asking Robert about a detach he had already checked). */
export function roofDayFor(timing: TradeTiming, roofStart: string | null, roofEnd: string | null): string | null {
  if (!roofStart || !isDateOnly(roofStart)) return null;
  return timing === "after_build" ? normalizeBuildEnd(roofStart, roofEnd) ?? roofStart : roofStart;
}

/** The flag a trade gets when its roof moves or is called off. */
export function roofMovedReason(oldStart: string | null, newStart: string | null, newEnd: string | null): string {
  if (!newStart) return `The roof was called off${oldStart ? ` (it was ${fmtShort(oldStart)})` : ""}. Check this date with the sub.`;
  if (!oldStart) return `The roof is now scheduled for ${fmtShortSpan(newStart, newEnd)}. Check this date with the sub.`;
  return `The roof moved from ${fmtShort(oldStart)} to ${fmtShortSpan(newStart, newEnd)}. Check this date with the sub.`;
}

// ── The Google event's description ──
// Jose and anyone with the Other Trades calendar read it, so it never carries a price (decision 8).

export interface TradeEventInput {
  homeowner: string | null;
  address: string | null;
  work: TradeWork;
  items: TradeItem[];
  /** Labels from the sub's price list, by key. */
  labels: Record<string, string>;
  units: Record<string, PriceUnit>;
  gutterSize?: string | null;
  color?: string | null;
  otherLabel?: string | null;
  timing: TradeTiming;
  roofStart: string | null;
  roofEnd: string | null;
  /** null = all day. */
  arrival: string | null;
  /** What we agreed (trade_items.scope_notes). */
  agreed: string | null;
  setUpBy: string;
  setUpByPhone: string | null;
  setUpOn: string;
  rms: string[];
  dealUrl: string;
}

export function tradeEventDescription(i: TradeEventInput): string {
  const out: string[] = [];
  out.push(displayName(i.homeowner));
  if (i.address) out.push(i.address.trim());
  out.push("");

  const work = i.work === "other" && (i.otherLabel ?? "").trim() ? (i.otherLabel ?? "").trim() : WORK_LABELS[i.work];
  out.push(`Work: ${work}`);
  if (i.timing !== "none") {
    if (!i.roofStart) out.push(`${i.timing === "before_build" ? "Before" : "After"} the roof build (not scheduled yet)`);
    else if (i.timing === "before_build") out.push(`Before the roof build on ${fmtLong(i.roofStart)}`);
    else out.push(`After the roof build on ${fmtLong(normalizeBuildEnd(i.roofStart, i.roofEnd) ?? i.roofStart)}`);
  }
  for (const it of i.items) {
    if (it.key === CUSTOM_ITEM) {
      if (it.label) out.push(`- ${it.label}`);
      continue;
    }
    const label = i.labels[it.key] ?? ITEM_LABELS[it.key] ?? it.key;
    const qty = fmtQty(it.qty, i.units[it.key] ?? itemUnit(it.key));
    out.push(qty ? `- ${label}: ${qty}` : `- ${label}`);
  }
  const look = [i.gutterSize ? `Size ${i.gutterSize}` : "", i.color ? `Color ${i.color}` : ""].filter(Boolean).join(" · ");
  if (look) out.push(look);
  if (i.arrival) out.push(`Arrival: ${i.arrival}`);

  const agreed = (i.agreed ?? "").trim();
  if (agreed) {
    out.push("");
    out.push("What we agreed:");
    out.push(agreed);
  }

  out.push("");
  out.push(`Questions: ${i.setUpBy}${i.setUpByPhone ? ` ${i.setUpByPhone}` : ""}`);
  if (i.rms.length) out.push(`RM: ${i.rms.join(", ")}`);
  out.push(`Set up by ${i.setUpBy} in the CRM on ${fmtMonthDay(i.setUpOn)}`);
  out.push(i.dealUrl);
  return out.join("\n").slice(0, 8000);
}

// ── Homeowner texts (docs/trade-scheduling-plan.md 3e, approved by Greg, Oct 28, 2026) ──
// Greg: "I know my gutter team is not always punctual. It's usually +/- a day." So a text gives a day "give
// or take a day" and never a time, and a detach is told against the roof date, the date the homeowner is
// watching. Plain characters only (GSM-7), like the build text.

const intro = (first: string) => `Hi ${first}, it's Peach Duck Contracting (Bardin Roofing).`;
const CLOSE = "You don't need to be home. Reply here with any questions. Reply STOP to opt out.";

export interface TradeTextTrade {
  work: TradeWork;
  items: TradeItem[];
  timing: TradeTiming;
  date: string;
  /** The trade_items row, so the server can mark told exactly the visits a text covered. */
  id?: string;
}

export interface TradeTextContext {
  firstName: string;
  street: string | null;
  roofStart: string | null;
  /** The roof's last day, when the build runs more than a day; the "after your roof build" texts are held for a
   *  visit on or before it (delta review of PR #76, Oct 29, 2026). */
  roofEnd?: string | null;
}

/** Why the "after your roof build" wording can't go for this day, or null when it can: the visit has to come
 *  after the roof's last day. No roof day = nothing to compare (the sheet warns against the expected date). */
export function afterRoofTextProblem(date: string | null, roofStart: string | null, roofEnd: string | null | undefined): string | null {
  if (!date || !roofStart || !isDateOnly(date) || !isDateOnly(roofStart)) return null;
  const last = normalizeBuildEnd(roofStart, roofEnd ?? null) ?? roofStart;
  if (date > last) return null;
  return `The approved text says the crew comes after your roof build; the roof ${last === roofStart ? "is" : "runs through"} ${fmtShort(last)} and this visit is ${fmtShort(date)}, so no text goes for it. Let the homeowner know yourself.`;
}

/** The text for one trade that just got its day, or null while Greg hasn't approved its wording: a detach on
 *  a roof with no booked date (plan 3e, "not yet approved", Oct 28, 2026). Put that wording back here once he
 *  OKs it. */
export function tradeScheduledText(c: TradeTextContext, t: TradeTextTrade): string | null {
  const crew = crewWord(t.items, t.work);
  const what = itemsPhrase(t.items, t.work);
  const at = (c.street ?? "").trim() ? ` at ${(c.street ?? "").trim()}` : "";
  const day = fmtLong(t.date);
  if (t.work === "detach") {
    if (!c.roofStart) return null;
    return `${intro(c.firstName)} Before your roof build on ${fmtLong(c.roofStart)}, our ${crew} will take down your ${what} so the roofers can work. They'll come by a day or two before. ${CLOSE}`;
  }
  // The work picks the text, not the timing (pre-deploy review, Oct 29, 2026): only a reset, or a new install
  // after the roof, takes the "After your roof build" line. A service call always reads as a service call,
  // whatever Alex tapped under "Against the roof", so a repair never tells the homeowner an install is coming.
  if (t.work === "reset" || (t.work === "new_install" && t.timing === "after_build")) {
    const job = t.work === "reset" ? `put your ${what} back up` : `install your new ${what}`;
    return `${intro(c.firstName)} After your roof build, our ${crew} will be out around ${day}, give or take a day, to ${job}. ${CLOSE}`;
  }
  if (t.work === "new_install") {
    return `${intro(c.firstName)} Our ${crew} will be out to install your new ${what}${at} around ${day}, give or take a day. ${CLOSE}`;
  }
  return `${intro(c.firstName)} Our ${crew} will be out to take care of your ${what}${at} around ${day}, give or take a day. ${CLOSE}`;
}

/** One text for a detach and the reset (or new install) after the roof, saved together. */
export function tradePairText(c: TradeTextContext & { roofStart: string }, detach: TradeTextTrade, after: TradeTextTrade): string {
  const crew = crewWord([...detach.items, ...after.items]);
  const day = fmtLong(after.date);
  const tail = after.work === "reset"
    ? `and put them back up around ${day}, give or take a day.`
    : `then install your new ${itemsPhrase(after.items)} around ${day}, give or take a day.`;
  return `${intro(c.firstName)} Before your roof build on ${fmtLong(c.roofStart)}, our ${crew} will take down your ${itemsPhrase(detach.items)} ${tail} ${CLOSE}`;
}

/** Why the approved detach text can't go for this day, or null when it can. That text tells the homeowner the
 *  crew comes "a day or two before" the roof, so it only fits a detach one or two days ahead of the roof's
 *  first day (pre-deploy review, Oct 29, 2026: a detach Jose could only do a week early was still told "a day
 *  or two before"). Any other day has no approved wording yet (plan 3e), so that detach goes untexted and the
 *  sheet says so. No roof day = nothing to compare. */
export function detachTextProblem(date: string | null, roofStart: string | null): string | null {
  if (!date || !roofStart || !isDateOnly(date) || !isDateOnly(roofStart)) return null;
  const gap = daysBetween(date, roofStart);
  if (gap >= 1 && gap <= 2) return null;
  return `The approved detach text says the crew comes a day or two before the roof; this detach is ${gap <= 0 ? "not before it" : `${gap} days before it`}, so no text goes for it. Let the homeowner know yourself.`;
}

export type TextSkipReason = "wording_pending" | "detach_day" | "roof_day" | "no_words";
export interface TextPlan {
  texts: string[];
  /** The trades the texts cover, in order: one per text, or both visits of a pair for its one text. */
  told: TradeTextTrade[];
  skipped: Array<{ trade: TradeTextTrade; reason: TextSkipReason; detail: string }>;
}

/** What a save texts: one text for a detach and its reset together when the roof has a day, otherwise one per
 *  trade. Only a trade with a day, work the approved texts can name (textCanNameWork), and approved wording
 *  gets one; the rest are listed with the reason so the sheet and the save result can say so. A detach + new
 *  install goes as two texts (the detach-only text, then the after-the-roof install one): the combined wording
 *  in tradePairText is still "not yet approved" in plan 3e. Make `after` any after-roof visit again once Greg
 *  OKs it. */
export function scheduledTextPlan(c: TradeTextContext, trades: TradeTextTrade[]): TextPlan {
  const skipped: TextPlan["skipped"] = [];
  const dated = trades.filter((t) => isDateOnly(t.date)).filter((t) => {
    if (textCanNameWork(t.work, t.items)) return true;
    skipped.push({ trade: t, reason: "no_words", detail: "No approved text names this work yet. Let the homeowner know yourself." });
    return false;
  });
  const detach = dated.find((t) => t.work === "detach");
  const after = dated.find((t) => t !== detach && t.timing === "after_build" && t.work === "reset");
  // The pair text says "before your roof build on <day> ... take down ... and put them back up around <day>":
  // only when the detach is before the roof and the reset after it (delta review of PR #76, Oct 29, 2026: a
  // detach Nov 2 and reset Nov 4 on a Nov 6 roof were texted as if they fit). Otherwise each visit is judged alone.
  if (detach && after && c.roofStart && dated.length === 2 && detach.date < c.roofStart && !afterRoofTextProblem(after.date, c.roofStart, c.roofEnd)) {
    return { texts: [tradePairText({ ...c, roofStart: c.roofStart }, detach, after)], told: [detach, after], skipped };
  }
  const texts: string[] = [];
  const told: TradeTextTrade[] = [];
  for (const t of dated) {
    const problem = t.work === "detach" ? detachTextProblem(t.date, c.roofStart) : null;
    if (problem) { skipped.push({ trade: t, reason: "detach_day", detail: problem }); continue; }
    const afterRoof = t.work === "reset" || (t.work === "new_install" && t.timing === "after_build") ? afterRoofTextProblem(t.date, c.roofStart, c.roofEnd) : null;
    if (afterRoof) { skipped.push({ trade: t, reason: "roof_day", detail: afterRoof }); continue; }
    const text = tradeScheduledText(c, t);
    if (text === null) { skipped.push({ trade: t, reason: "wording_pending", detail: "The text for a detach with no roof date booked isn't approved yet. Let the homeowner know yourself." }); continue; }
    texts.push(text);
    told.push(t);
  }
  return { texts, told, skipped };
}

/** The texts a save sends (scheduledTextPlan's texts). */
export function scheduledTexts(c: TradeTextContext, trades: TradeTextTrade[]): string[] {
  return scheduledTextPlan(c, trades).texts;
}

/** The trades told once `sent` of the plan's texts went out: Twilio can take the first text and refuse the
 *  second (pre-deploy review, Oct 29, 2026), and only a told trade's Move says "moved" or "called off". */
export function toldAfter(plan: TextPlan, sent: number): TradeTextTrade[] {
  if (sent <= 0) return [];
  if (plan.texts.length === 1 && plan.told.length === 2) return plan.told; // the pair's one text covers both
  return plan.told.slice(0, sent);
}

/** `work` keeps the crew word in step with the scheduled text (a soffit / fascia job is "our crew" either way). */
export function tradeMovedText(firstName: string, items: TradeItem[], date: string, work?: TradeWork): string {
  return `Hi ${firstName}, it's Peach Duck Contracting. Our ${crewWord(items, work)} will now be out around ${fmtLong(date)}, give or take a day. Reply here with any questions.`;
}

export function tradeCalledOffText(firstName: string, items: TradeItem[], oldDate: string, work?: TradeWork): string {
  const visit = crewWord(items, work) === "gutter crew" ? "gutter visit" : "visit";
  return `Hi ${firstName}, it's Peach Duck Contracting. The ${visit} for ${fmtLong(oldDate)} is off for now. We'll text you the new day once it's set.`;
}

/** Calendar days between two date-only strings (b - a). */
export function daysBetween(a: string, b: string): number {
  return Math.round((Date.UTC(+b.slice(0, 4), +b.slice(5, 7) - 1, +b.slice(8, 10)) - Date.UTC(+a.slice(0, 4), +a.slice(5, 7) - 1, +a.slice(8, 10))) / 86400000);
}

/** What a move tells the homeowner. `toldDate` is the day they were last texted (null = never told).
 *  A move of a day or less sends nothing: they were told "give or take a day". */
export function moveTextKind(toldDate: string | null, newDate: string | null): "scheduled" | "moved" | "called_off" | "none" {
  if (!newDate) return toldDate ? "called_off" : "none";
  if (!toldDate) return "scheduled";
  return Math.abs(daysBetween(toldDate, newDate)) <= 1 ? "none" : "moved";
}

/** Who an event keeps when the sheet saves an edit (pre-deploy review of PR #76, Oct 29, 2026): everyone
 *  already on it stays, hand-added guests included, since the sheet only knows the defaults; a default the
 *  sheet left unticked is the one thing an edit takes off; a ticked guest not yet on the event is added.
 *  `sent` empty (a Move sends no list) keeps the event's guests as they are. */
export function mergeGuests(had: string[], sent: string[], defaults: string[]): string[] {
  const off = new Set(sent.length ? defaults.filter((e) => !sent.includes(e)) : []);
  const seen = new Set<string>();
  return [...had, ...sent].filter((e) => !off.has(e) && !seen.has(e) && (seen.add(e), true));
}
